/**
 * OPENING CONFIGURATION GOVERNANCE round (2026-08-10, docs/MODELER.md): door/window
 * width/height are INSTANCE params constrained to a curated `sizeSeries` TYPE param
 * (a real-vendor-size catalog) — "if it's not in the library, it's a custom order."
 * An off-series size is allowed but flagged as a DEVIATION everywhere the app surfaces
 * project-local/firm-standard drift (the palette size field, the Library panel's
 * deviations line, eventually the opening schedule).
 *
 * Two resolution paths are exported because deviation counting happens both against a
 * full ResolvedElement (the palette, mid-drawing) and against raw ElementRecord/
 * TypeRecord pairs (LibraryPanel's flat lists, which never resolve every element).
 */
import type { CategoryId, ParamValue } from './types'
import type { ResolvedElement } from './params'

const SIZE_EPS = 1e-6

/** Normalize a type's raw `sizeSeries` value into a clean entry list — never throws
 *  on malformed/legacy data (an unset or pre-round type has no series yet). */
export function sizeSeriesEntries(raw: ParamValue | undefined): { widthFt: number; heightFt: number }[] {
  if (!Array.isArray(raw)) return []
  return (raw as unknown[]).filter((e): e is { widthFt: number; heightFt: number } =>
    !!e && typeof e === 'object' && typeof (e as { widthFt?: unknown }).widthFt === 'number'
    && typeof (e as { heightFt?: unknown }).heightFt === 'number')
}

export function matchesSizeSeries(
  series: { widthFt: number; heightFt: number }[],
  widthFt: number,
  heightFt: number,
): boolean {
  return series.some((e) => Math.abs(e.widthFt - widthFt) < SIZE_EPS && Math.abs(e.heightFt - heightFt) < SIZE_EPS)
}

/** True when a resolved door/window's effective size is OFF its type's curated series
 *  (false when the type has no series yet — nothing to deviate from, or the category
 *  isn't an opening). */
export function isCustomOpeningSize(el: ResolvedElement): boolean {
  if (el.category !== 'door' && el.category !== 'window') return false
  const series = sizeSeriesEntries(el.typeParams.sizeSeries)
  if (!series.length) return false
  const w = el.params.widthFt as number
  const h = el.params.heightFt as number
  if (typeof w !== 'number' || typeof h !== 'number') return false
  return !matchesSizeSeries(series, w, h)
}

/** Raw-record variant for contexts without a full ResolvedElement (e.g. LibraryPanel's
 *  flat element/type lists) — same rule, fed the element's own params + its type's raw
 *  params directly instead of a resolved merge. */
export function isRawOpeningCustomSize(
  category: CategoryId,
  elParams: Record<string, ParamValue>,
  typeParams: Record<string, ParamValue>,
): boolean {
  if (category !== 'door' && category !== 'window') return false
  const series = sizeSeriesEntries(typeParams.sizeSeries)
  if (!series.length) return false
  const w = elParams.widthFt as number
  const h = elParams.heightFt as number
  if (typeof w !== 'number' || typeof h !== 'number') return false
  return !matchesSizeSeries(series, w, h)
}
